import { zodResolver } from '@hookform/resolvers/zod'
import {
  Calendar,
  Globe,
  MessageSquare,
  Music2,
  RefreshCcw,
  Sparkles,
  Telescope,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useStartNasaApod, type NasaApodInput } from '@/api/endpoints/factories'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  date: 'Data',
  language: 'Idioma',
  music_mood: 'Trilha',
  end_text: 'CTA final',
  tone: 'Tom',
  force: 'Forçar',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type MoodKey = 'mysterious' | 'epic' | 'calm' | 'tense' | 'fun'
const MOODS: ReadonlyArray<{ value: MoodKey; label: string }> = [
  { value: 'mysterious', label: 'Misterioso (padrão)' },
  { value: 'epic', label: 'Épico' },
  { value: 'calm', label: 'Calmo' },
  { value: 'tense', label: 'Tenso' },
  { value: 'fun', label: 'Divertido' },
]

type ToneKey = 'formal' | 'curious' | 'sarcastic'

// Radix's <Select.Item> forbids ``value=""`` (it reserves the empty string
// for clearing the selection internally), so the "no override" slot uses
// the sentinel ``'default'`` and is converted back to ``''`` on submit
// — same pattern as ``AUTO_SUBREDDIT`` in RedditStoriesForm.
const TONE_DEFAULT = 'default' as const
const TONES: ReadonlyArray<{ value: ToneKey | typeof TONE_DEFAULT; label: string }> = [
  { value: TONE_DEFAULT, label: 'Maravilhamento (padrão)' },
  { value: 'formal', label: 'Formal' },
  { value: 'curious', label: 'Curioso' },
  { value: 'sarcastic', label: 'Debochado' },
]

// APOD started 1995-06-16. Don't let users pick before that — the API just
// returns 404s and the year-fallback walks back further than it should.
const APOD_MIN_DATE = '1995-06-16'

function todayISO(): string {
  const d = new Date()
  const yyyy = String(d.getFullYear())
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const schema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  language: z.enum(['pt-br', 'en', 'both']),
  music_mood: z.enum(['mysterious', 'epic', 'calm', 'tense', 'fun']),
  end_text: z.string().trim().max(140, 'Máximo 140 caracteres').optional(),
  tone: z.enum(['', 'formal', 'curious', 'sarcastic']).optional(),
  force: z.boolean().optional(),
  tts_voice_pt: z.string().optional(),
  tts_voice_en: z.string().optional(),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  date: '',
  language: 'pt-br',
  music_mood: 'mysterious',
  end_text: '',
  tone: '',
  force: false,
  tts_voice_pt: '',
  tts_voice_en: '',
}

/** Form snapshot → preset payload. ``date`` is intentionally NOT carried —
 * a preset stamped with a fixed date wouldn't make sense for the cron's
 * recurring runs. */
function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    language: values.language,
    music_mood: values.music_mood,
  }
  if (values.end_text?.trim()) payload.end_text = values.end_text.trim()
  if (values.tone) payload.tone = values.tone
  if (values.force) payload.force = true
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const v: FormValues = {
    ...FORM_DEFAULTS,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    music_mood:
      (params.music_mood as FormValues['music_mood'] | undefined) ?? FORM_DEFAULTS.music_mood,
    end_text: (params.end_text as string | undefined) ?? '',
    force: Boolean(params.force ?? false),
  }
  if (params.tone) v.tone = params.tone as FormValues['tone']
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function NasaApodForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const startMutation = useStartNasaApod()
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: todayISO(),
      language: 'pt-br',
      music_mood: 'mysterious',
      end_text: '',
      tone: '',
      force: false,
      tts_voice_pt: '',
      tts_voice_en: '',
      llm_provider: 'auto',
    },
  })

  const language = form.watch('language')
  const mood = form.watch('music_mood')
  const tone = form.watch('tone')
  const force = form.watch('force')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: NasaApodInput = {
      date: values.date?.trim() ? values.date.trim() : null,
      language: values.language,
      channel: channel || null,
      music_mood: values.music_mood,
      end_text: values.end_text?.trim() ?? '',
      tone: values.tone ? values.tone : null,
      force: values.force ?? false,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Telescope aria-hidden /> APOD · NASA
          </div>
          <h1 className={styles.title}>Que dia do céu vamos mostrar?</h1>
          <p className={styles.subtitle}>
            Escolha a data — o Prism baixa a foto astronômica do dia, narra a explicação da NASA
            e monta um Short com Ken Burns. Em dias de vídeo, ele recua MM-DD em até 20 anos
            até achar uma imagem.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="nasa_apod"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                // Hydrate but keep whichever date the user already typed —
                // presets don't pin a date.
                const currentDate = form.getValues('date')
                form.reset({ ...values, date: currentDate ?? '' })
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Calendar />} label="Data" htmlFor="apod-date">
              <Input
                id="apod-date"
                type="date"
                min={APOD_MIN_DATE}
                max={todayISO()}
                placeholder="Hoje"
                error={Boolean(form.formState.errors.date)}
                {...form.register('date')}
              />
              {form.formState.errors.date ? (
                <span className={styles.errorText}>{form.formState.errors.date.message}</span>
              ) : null}
            </Row>

            <Row icon={<Globe />} label="Idioma">
              <Segmented<'pt-br' | 'en' | 'both'>
                options={LANGUAGES}
                value={language}
                onValueChange={(v) => {
                  form.setValue('language', v)
                }}
                aria-label="Idioma"
              />
            </Row>

            <Row icon={<Music2 />} label="Trilha" htmlFor="apod-mood">
              <SelectRoot
                value={mood}
                onValueChange={(v) => {
                  form.setValue('music_mood', v as MoodKey)
                }}
              >
                <SelectTrigger id="apod-mood" aria-label="Trilha">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Wand2 />} label="Tom da narração">
                  <SelectRoot
                    value={tone || TONE_DEFAULT}
                    onValueChange={(v) => {
                      form.setValue('tone', v === TONE_DEFAULT ? '' : (v as ToneKey))
                    }}
                  >
                    <SelectTrigger aria-label="Tom">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectRoot>
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="CTA final (opcional)">
                  <Input
                    placeholder="Ex.: Segue pra mais astronomia."
                    error={Boolean(form.formState.errors.end_text)}
                    {...form.register('end_text')}
                  />
                  {form.formState.errors.end_text ? (
                    <span className={styles.errorText}>
                      {form.formState.errors.end_text.message}
                    </span>
                  ) : null}
                </AdvSection>

                <AdvSection icon={<RefreshCcw />} label="Forçar regeneração">
                  <label className={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={force ?? false}
                      onChange={(e) => {
                        form.setValue('force', e.target.checked)
                      }}
                    />
                    <span>Regerar mesmo se essa (data, idioma) já foi produzida.</span>
                  </label>
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="nasa_apod" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}
